import { Link } from "@tanstack/react-router";
import { MONTH_NAMES } from "@rigo/nepali-calendar";
import { Alert, SectionCard, PageWrapper } from "@/deps/ui-library";
import { useCountryPack } from "@/api/queries";
import type { CountryPack } from "@/api/types";
import { usePackName, useTaxWords } from "@/app/session";
import { PageHeader } from "@/components/PageHeader";

/** "Shrawan 1", or "April 1" under the Gregorian calendar. */
function yearStart(pack: CountryPack): string {
  const month =
    pack.calendar === "bikramSambat"
      ? MONTH_NAMES[pack.fiscalYearStartsMonth - 1]
      : new Date(Date.UTC(2026, pack.fiscalYearStartsMonth - 1, 1)).toLocaleDateString("en-GB", {
          month: "long",
          timeZone: "UTC",
        });
  return `${month} ${pack.fiscalYearStartsDay}`;
}

/**
 * What the country pack set for this company. It's read-only because a posted document depends on
 * these, and the names and categories a company can change are on Tax codes.
 */
export function CountryPackPage() {
  const say = useTaxWords();
  const packName = usePackName();
  const { data: pack, isPending, error } = useCountryPack();

  const rows: Array<[string, string]> = pack
    ? [
        ["Currency", pack.currencyCode],
        [
          "Rounding",
          `${pack.roundingScale} decimal places, ${
            pack.roundingAwayFromZero ? "halves away from zero" : "halves to even"
          }`,
        ],
        ["Number format", pack.grouping === "lakhCrore" ? "Lakh and crore" : "Thousands"],
        [
          "Calendar",
          pack.calendar === "bikramSambat"
            ? "Bikram Sambat on screen. The ledger always stores Gregorian dates."
            : "Gregorian",
        ],
        ["Fiscal year starts", yearStart(pack)],
      ]
    : [];

  return (
    <PageWrapper spacing="md" className="p-0">
      <PageHeader title="Country pack" />
      {error ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "warning" }}
          description="No country pack is applied to this company yet."
        />
      ) : null}
      {isPending ? <p className="text-fg-muted text-sm">Loading the country pack…</p> : null}
      {pack ? (
        <SectionCard title={packName ? `Country pack · ${packName}` : "Country pack"}>
          <dl className="flex flex-col gap-2 text-sm">
            {rows.map(([label, value]) => (
              <div key={label} className="flex flex-wrap gap-x-6">
                <dt className="text-fg-muted w-40">{label}</dt>
                <dd className="text-foreground">{value}</dd>
              </div>
            ))}
          </dl>
        </SectionCard>
      ) : null}
      <SectionCard title={say("Tax codes and TDS categories")}>
        <p className="text-sm">
          <Link to="/settings" search={{ tab: "tax" }} className="text-primary underline">
            Open Tax codes
          </Link>
        </p>
      </SectionCard>
    </PageWrapper>
  );
}
